import api from "../services/api";



/**

 * Softphone dual-mode:

 * - token mfv_* → widget embutido no Multiflow (iframe /softphone?embed=1)

 * - demais tokens → WaVoIP widget oficial

 *

 * A URL do painel Multiflow Voz vem das Settings do master (chave multiflowVozUrl).

 * Dev local: multiflowVozUrl = http://localhost:4173 (UI + proxy Vite → API :4100).

 */



const CACHE_KEY = "MULTIFLOW_VOZ_URL";

let memoryCache = "";



export const MFV_SOFTPHONE_EVENT = "mfv-softphone";



export function isMultiflowVozToken(token) {

  return typeof token === "string" && token.trim().toLowerCase().startsWith("mfv_");

}



export async function resolveMultiflowVozBaseUrl() {

  if (memoryCache) {

    return memoryCache.replace(/\/$/, "");

  }



  const fromLs = localStorage.getItem(CACHE_KEY);

  if (fromLs) {

    memoryCache = fromLs;

    return fromLs.replace(/\/$/, "");

  }



  try {

    const { data } = await api.get("/setting-global/multiflowVozUrl");

    const url = typeof data === "string" ? data : data?.value || data?.url || "";

    if (url && String(url).trim()) {

      memoryCache = String(url).trim().replace(/\/$/, "");

      localStorage.setItem(CACHE_KEY, memoryCache);

      return memoryCache;

    }

  } catch (err) {

    console.warn("Não foi possível carregar multiflowVozUrl das Settings:", err?.message || err);

  }



  return "";

}



export function setMultiflowVozBaseUrlCache(url) {

  memoryCache = String(url || "").trim().replace(/\/$/, "");

  if (memoryCache) localStorage.setItem(CACHE_KEY, memoryCache);

  else localStorage.removeItem(CACHE_KEY);

}



/** @deprecated use resolveMultiflowVozBaseUrl */

export function getMultiflowVozBaseUrl() {

  return (memoryCache || localStorage.getItem(CACHE_KEY) || "").replace(/\/$/, "");

}



export function buildMultiflowVozSoftphoneUrl({

  base,

  token,

  phone = "",

  name = "",

  embed = false,

  startIfReady = false,

  hostChrome = false,

  rejectCalls = false,

  theme = "",

  multiflowApiUrl = "",

  /** API/HUB VOZ quando softphone e hub estão em origens diferentes */

  vozApiBase = "",

  /** Fila(s) do atendente (pós-URA só nestas) — "1,2,3" ou number[] */

  agentQueueIds = null,

  /** ID do usuário CRM (filtra transferência dirigida) */
  agentUserId = null,

}) {

  const qs = new URLSearchParams({

    token: token || "",

    phone: String(phone || "").replace(/\D/g, ""),

    name: name || "",

  });

  if (embed) qs.set("embed", "1");

  if (startIfReady) qs.set("start_if_ready", "1");

  if (hostChrome) qs.set("host", "1");

  if (rejectCalls) qs.set("reject_calls", "1");

  if (theme === "light" || theme === "dark") qs.set("theme", theme);

  const mf =

    String(multiflowApiUrl || process.env.REACT_APP_BACKEND_URL || "")

      .trim()

      .replace(/\/$/, "");

  if (mf) qs.set("mf_api", mf);

  // Filtro de fila no softphone (URA pós-setor)
  let qIds = agentQueueIds;
  if (Array.isArray(qIds)) {
    qIds = qIds
      .map((n) => Number(n))
      .filter((n) => n > 0)
      .join(",");
  }
  if (qIds && String(qIds).trim()) {
    qs.set("queues", String(qIds).replace(/\s+/g, ""));
  }

  const uid = Number(agentUserId);
  if (Number.isFinite(uid) && uid > 0) {
    qs.set("user_id", String(uid));
  }


  const origin = String(base || "").trim().replace(/\/$/, "");

  const hub = String(vozApiBase || "").trim().replace(/\/$/, "");

  // Same origin (ex.: localhost:4173 UI + proxy /api): sem ?api=

  if (hub && origin && hub !== origin) {

    qs.set("api", hub);

  }



  return `${origin}/softphone?${qs.toString()}`;

}



/**

 * Resolve origem do softphone. Override opcional:

 * REACT_APP_MFV_SOFTPHONE_ORIGIN ou localStorage mfv_softphone_origin

 */

export function resolveSoftphoneUiOrigin(vozHubBase = "") {

  let override = "";

  try {

    override = String(

      process.env.REACT_APP_MFV_SOFTPHONE_ORIGIN ||

        localStorage.getItem("mfv_softphone_origin") ||

        ""

    ).trim();

  } catch {

    override = String(process.env.REACT_APP_MFV_SOFTPHONE_ORIGIN || "").trim();

  }



  const hub = String(vozHubBase || "").replace(/\/$/, "");

  const ui = (override || hub).replace(/\/$/, "");

  return {

    softphoneBase: ui,

    vozHubBase: hub,

    usedOverride: Boolean(override),

  };

}



function dispatchSoftphone(detail) {

  if (typeof window === "undefined") return;

  window.dispatchEvent(new CustomEvent(MFV_SOFTPHONE_EVENT, { detail }));

}



/** Abre o softphone embutido (igual WaVoIP dentro do sistema). */

export async function openMultiflowVozSoftphone({

  token,

  phone = "",

  name = "",

  startIfReady = false,

} = {}) {

  const base = await resolveMultiflowVozBaseUrl();

  if (!base) {

    throw new Error(

      "URL do Multiflow Voz não configurada. O usuário master deve definir em Configurações → Multiflow Voz."

    );

  }

  const { softphoneBase, vozHubBase } = resolveSoftphoneUiOrigin(base);

  const url = buildMultiflowVozSoftphoneUrl({

    base: softphoneBase || base,

    vozApiBase: vozHubBase || base,

    token,

    phone,

    name,

    embed: true,

    startIfReady,

  });

  dispatchSoftphone({

    action: "open",

    token,

    phone,

    name,

    startIfReady,

  });

  return url;

}



export function toggleMultiflowVozSoftphone({ token, phone = "", name = "" } = {}) {

  dispatchSoftphone({ action: "toggle", token, phone, name });

}



export function closeMultiflowVozSoftphone() {

  dispatchSoftphone({ action: "close" });

}



/**

 * Click-to-call unificado. Retorna "voz" | "wavoip".

 */

export async function startVoiceCall({ token, phone, name, onWavoip }) {

  if (!token) throw new Error("Token de voz não disponível nesta conexão.");

  if (isMultiflowVozToken(token)) {

    await openMultiflowVozSoftphone({ token, phone, name, startIfReady: true });

    return "voz";

  }

  if (typeof onWavoip === "function") {

    await onWavoip();

    return "wavoip";

  }

  throw new Error("Softphone WaVoIP indisponível.");

}

